import React from 'react'
import { Map, List } from 'immutable'
import _get from 'lodash/get'
import _set from 'lodash/set'
import { get as getConfig } from 'config'
import {
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_VIDEO,
  TYPE_CONTENT_PRODUCT,
  isGuideType,
  isEventType,
} from 'services/content-type'
import { parse as parseQuery, stringify as stringifyQuery } from 'services/query-string'
import GaiaLogo, { TYPE_WHITE } from 'components/GaiaLogo'
import Link from 'components/Link'
import { TARGET_BLANK } from 'components/Link/constants'
import IconV2, { ICON_TYPES } from 'components/Icon.v2'
import ProgressBar, { PROGRESS_BAR_TYPE_SOLID } from 'components/ProgressBar'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from 'components/Picture'
import { URL_GAIA_PLUS } from 'services/url/constants'
import {
  upgradeToWatchCheck,
  featureAllowedWithUserSubscription,
  getPlayableUrl,
  getFeatureMedia,
} from 'services/content'
import {
  handleTileV2OnClick,
  isGaiaOriginal,
  getGraphqlHeroImage,
  getImageSizesGraphql,
  getTileV2ElementClassName,
} from '../utils'

import {
  TILE_V2_DISPLAY_DEFAULT,
} from '../constants'

const config = getConfig()
const optimizedImagesEnabled = !!_get(config, ['features', 'optimizedImages'])

/**
 * The progress bar for a tile (series or video)
 * @param {Object} props - The props
 * @param {String} props.contentType - The type of content
 * @returns {JSX.Element|null} Progress bar for a tile or null
 */
function TileV2GraphalProgressBar (props) {
  const {
    contentType,
  } = props

  if (contentType !== TYPE_CONTENT_SERIES && contentType !== TYPE_CONTENT_VIDEO) {
    return null
  }

  const featureMedia = getFeatureMedia(props)
  const featureMediaDuration = featureMedia.get('duration', 0) || 0
  const userMediaPosition = featureMedia.getIn(['userMediaPosition', 'position'], 0) || 0

  if (featureMediaDuration && userMediaPosition) {
    return (
      <div className="tile-v2__progress-bar">
        <ProgressBar
          duration={featureMediaDuration}
          userInfo={Map({ featurePosition: userMediaPosition })}
          type={PROGRESS_BAR_TYPE_SOLID}
        />
      </div>
    )
  }

  return null
}

/**
 * The play button for touch devices
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Boolean} props.isTouchable - Whether or not the device is touchable
 * @param {String} props.contentType - The type of content
 * @param {Boolean} props.upgradeToWatch - Whether or not the user needs to upgrade to watch
 * @param {Boolean} props.watchAccess - Whether or not the user has access to watch
 * @param {String} props.path - The url of the content
 * @returns {JSX.Element|null} Play/action button for touch devices
 */
function TileV2GraphqlTouchablePlayButton (props) {
  const {
    isTouchable,
    contentType,
    path,
    watchAccess,
    upgradeToWatch,
    data,
  } = props

  if (!data) {
    return null
  }

  if (contentType !== TYPE_CONTENT_SERIES && contentType !== TYPE_CONTENT_VIDEO) {
    return null
  }

  if (isTouchable && upgradeToWatch) {
    return (
      <Link
        className="tile-v2__touch-action-btn"
        to={URL_GAIA_PLUS}
      >
        <IconV2 type={ICON_TYPES.PADLOCK} />
      </Link>
    )
  }

  if (isTouchable && watchAccess) {
    return (
      <Link
        className="tile-v2__touch-action-btn"
        to={`${path}?fullplayer=feature`}
        onClick={(e) => handleTileV2OnClick(e, props, true)}
      >
        <IconV2 type={ICON_TYPES.PLAY} />
      </Link>
    )
  }

  return null
}

/**
 * Label for a tile (Gaia original, etc.)
 * @param {Object} props - The props
 * @param {Map} props.staticText - The static text
 * @param {String} props.contentType - The type of content
 * @returns {JSX.Element|null} Tile label
 */
function TileV2GrapqlLabel (props) {
  const {
    staticText,
    gaiaOriginal,
    upgradeToWatch,
    contentType,
    isGuide,
    isEvent,
  } = props

  if (isEvent || isGuide || upgradeToWatch) {
    return (
      <span className="tile-v2__label">
        <span className="tile-v2__label-text">
          {staticText.get('eventsPlus')}
        </span>
      </span>
    )
  }

  if (gaiaOriginal) {
    return (
      <span className="tile-v2__label">
        <GaiaLogo
          type={TYPE_WHITE}
          className={['tile-v2__label-logo']}
          isHref={false}
        />
        <span className="tile-v2__label-text tile-v2__label-text--original">
          {staticText.get('original')}
        </span>
      </span>
    )
  }

  if (contentType === TYPE_CONTENT_SERIES) {
    return (
      <span className="tile-v2__label">
        <span className="tile-v2__label-text">
          {staticText.get('series')}
        </span>
      </span>
    )
  }

  return null
}

/**
 * The tile hero image for a tile
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {String} props.display - The display type (normal, vertical, etc.)
 * @param {String} props.contentType - The type of content
 * @param {Boolean} props.hovered - Whether or not the tile is in the over state
 * @param {Boolean} props.isTouchable - Whether or not the device is touch capable
 * @param {Map} props.userNodeInfo - The userNodeInfo for the content
 * @param {List} props.subscriptions - The users entitlements
 * @param {Boolean} props.legacyData - Whether or not the data source is coming from a
 * @param {Boolean} props.hideLabel - Whether or not hide label
 * @returns {JSX.Element} The tile hero image
 */
function TileV2GraphqlHero (props) {
  const {
    data = Map(),
    display = TILE_V2_DISPLAY_DEFAULT,
    contentType,
    contentSubtype,
    staticText,
    hovered,
    isTouchable,
    subscriptions = List(),
    hideLabel = false,
  } = props
  const image = getGraphqlHeroImage(props)
  const title = data.get('title', '')
  const url = data.get('path', '')
  const ctaProductUrl = data.get('ctaProductUrl')
  const isContentTypeProduct = contentType === TYPE_CONTENT_PRODUCT
  const studio = data.get('studio', '')
  const gaiaOriginal = isGaiaOriginal({ studio }) && contentType === TYPE_CONTENT_SERIES
  const isGuide = isGuideType(data)
  const isEvent = isEventType(data)
  const imageSizes = getImageSizesGraphql(props)
  const imageWidth = _get(imageSizes, 'width', '400')
  const imageHeight = _get(imageSizes, 'height', '225')
  let imageUrl = ''
  let fallbackImageUrl = ''

  try {
    if (optimizedImagesEnabled) {
      const fallbackImageUrlObject = new URL(image)
      const imageUrlObject = new URL(image)
      const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
      const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
      const fallbackImageOptions = {
        ...parsedFallbackImageQuery,
        w: imageWidth,
        h: imageHeight,
        fm: 'jpg',
        compress: 'auto',
      }
      const imageOptions = {
        ...parsedImageQuery,
        w: imageWidth,
        h: imageHeight,
        fm: 'webp',
        q: '100',
      }

      const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
      const stringifiedImageQuery = stringifyQuery(imageOptions)

      // set the search string back onto the fallback URL
      _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)

      // set the search string back onto the image URL
      _set(imageUrlObject, 'search', stringifiedImageQuery)

      fallbackImageUrl = fallbackImageUrlObject.toString()
      imageUrl = imageUrlObject.toString()
    }
  } catch (e) {
    fallbackImageUrl = image
    imageUrl = image
  }

  const feature = getFeatureMedia(props)
  const watchAccess = !!feature.getIn(['contentPolicy', 'accessCheck', 'allowed'], false)
  const isFeatureAllowed = featureAllowedWithUserSubscription(props)
  const upgradeToWatch = upgradeToWatchCheck({
    feature,
    subscriptions,
    isFeatureAllowed,
  })

  return (
    <div className={
      getTileV2ElementClassName({
        display,
        hovered,
        contentType,
        baseClassName: 'tile-v2__hero',
      })
    }>
      {
        !hideLabel
          && <TileV2GrapqlLabel
                gaiaOriginal={gaiaOriginal}
                staticText={staticText}
                contentSubtype={contentSubtype}
                contentType={contentType}
                upgradeToWatch={upgradeToWatch}
                isGuide={isGuide}
                isEvent={isEvent}
              />
      }
      <Link
        to={isContentTypeProduct ? ctaProductUrl : url}
        className={
          getTileV2ElementClassName({
            display,
            hovered,
            contentType,
            baseClassName: 'tile-v2__hero-link',
          })
        }
        scrollToTop
        onClick={(e) => handleTileV2OnClick(e, props, false, 'Tile Thumbnail')}
        target={isContentTypeProduct ? TARGET_BLANK : null}
        directLink={isContentTypeProduct}
      >
        {
          optimizedImagesEnabled
            ? <Picture
                fallbackSrc={fallbackImageUrl}
                width={imageWidth}
                height={imageHeight}
                alt={title}
                imageClassName={
                  getTileV2ElementClassName({
                    display,
                    hovered,
                    contentType,
                    baseClassName: 'tile-v2__hero-image',
                  })
                }
                loading={IMAGE_LOADING_LAZY}
                sources={[
                  {
                    type: IMAGE_TYPE_WEBP,
                    srcSet: imageUrl,
                  },
                ]}
              />
            : <img
                src={image}
                loading="lazy"
                className={
                  getTileV2ElementClassName({
                    display,
                    hovered,
                    contentType,
                    baseClassName: 'tile-v2__hero-image',
                  })
                }
                alt={title}
              />
        }
      </Link>
      <TileV2GraphqlTouchablePlayButton
        {...props}
        isTouchable={isTouchable}
        contentType={contentType}
        path={getPlayableUrl(props)}
        watchAccess={watchAccess}
        upgradeToWatch={upgradeToWatch}
      />
      <TileV2GraphalProgressBar {...props} />
    </div>
  )
}

function tileV2GraphqlHeroPropsAreEqual (prevProps, nextProps) {
  const prevData = prevProps.data
  const nextData = nextProps.data
  const prevTouchable = prevProps.isTouchable
  const nextTouchable = nextProps.isTouchable

  return prevData.equals(nextData) && prevTouchable === nextTouchable
}

const MemoizedTileV2GraphqlHero = React.memo(TileV2GraphqlHero, tileV2GraphqlHeroPropsAreEqual)

export default MemoizedTileV2GraphqlHero
